import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, useFormik } from 'formik';
import * as yup from 'yup';
import ModalData from '../reviewProducts/Modal';
import { updateFormData, nextStep, setFileInfo } from '../../../Store/features/Units/unitsSlice';
import { addUnitFinancialData } from '../../../network/apis';

export default function OwnerDataForm({
  handlePrev,
  t,
  viewMode,
  isEditMode,
  editData,
  submitFormValues,
  setSubmitting,
  fromPage,
  open,
  onClose,
  onReject,
  handleOpenRejectDialog,
  rejectReason,
  setRejectReason,
  userId,
  managerId,
  isManager,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formData = useSelector((state) => state.unitform.formData);

  const initialListingType = editData?.listingType || formData.listingType || (editData?.rentPrice ? 'RENT' : 'SALE');

  const initialValues = {
    rentPrice: editData?.rentPrice || formData.rentPrice || '',
    insurancePrice: editData?.insurancePrice || formData.insurancePrice || '',
    ...formData,
    ...editData,
    listingType: initialListingType,
    PIECE_AREA: editData?.area || formData.area || '',
    PIECE_PRICE: editData?.piecePrice || formData.piecePrice || '',
    realEstateTax: editData?.realEstateTax || formData.realEstateTax || '',
    ownershipFees: editData?.ownershipFees || formData.ownershipFees || '',
    meterPrice: editData?.meterPrice || formData.meterPrice || '',
    totalPrice: editData?.totalPrice || formData.totalPrice || '',
    totalPriceNoVat: editData?.totalPriceNoVat || formData.totalPriceNoVat || '',
    commissionFees: editData?.commissionFees || formData.commissionFees || '',
    commissionFeesVat: editData?.commissionFeesVat || formData.commissionFeesVat || '',
  };

  // Define formik using the useFormik hook
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: viewMode
      ? undefined
      : yup.object({
          // Conditional validation based on listingType (SALE vs RENT)
          rentPrice: yup.string().when('listingType', {
            is: 'RENT',
            then: (schema) =>
              schema.required(t('General.required_field')).test('is-valid-number', t('General.number_validation'), (value) => {
                if (!value) return false;
                const formattedValue = parseFloat(value?.replace?.(',', '.') || value);
                return !Number.isNaN(formattedValue) && formattedValue >= 0;
              }),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          insurancePrice: yup.string().when('listingType', {
            is: 'RENT',
            then: (schema) =>
              schema.required(t('General.required_field')).test('is-valid-number', t('General.number_validation'), (value) => {
                if (!value) return false;
                const formattedValue = parseFloat(value?.replace?.(',', '.') || value);
                return !Number.isNaN(formattedValue) && formattedValue >= 0;
              }),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
        }),
    onSubmit: (values) => {
      dispatch(updateFormData(values));
      submitFormValues(values);
      if (setSubmitting) setSubmitting(false);
    },
  });

  const [loading, setLoading] = useState(false);
  const [inputChanged, setInputChanged] = useState(false);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value);

    if (name === 'PIECE_PRICE') {
      const price = parseFloat(value);
      if (!Number.isNaN(price)) {
        formik.setFieldValue('realEstateTax', (price * 0.05).toFixed(2));
      } else {
        formik.setFieldValue('realEstateTax', '');
      }
    }

    if (name === 'PIECE_AREA' || name === 'PIECE_PRICE') {
      const area = name === 'PIECE_AREA' ? value : formik.values.PIECE_AREA;
      const price = name === 'PIECE_PRICE' ? value : formik.values.PIECE_PRICE;
      if (area && price) {
        setInputChanged(true);
      }
    }

    if (formik.values.listingType === 'RENT') {
      if (name === 'rentPrice' || name === 'insurancePrice') {
        const rent = name === 'rentPrice' ? value : formik.values.rentPrice;
        if (rent) {
          setInputChanged(true);
        }
      }
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (inputChanged) {
      const timeoutId = setTimeout(() => {
        const isRent = formik.values.listingType === 'RENT';
        const hasSaleValues = formik.values.PIECE_AREA && formik.values.PIECE_PRICE;
        const hasRentValues = formik.values.PIECE_AREA && formik.values.rentPrice;

        if ((!isRent && hasSaleValues) || (isRent && hasRentValues)) {
          setLoading(true);
          addUnitFinancialData({
            PIECE_PRICE: isRent ? formik.values.rentPrice : formik.values.PIECE_PRICE,
            PIECE_AREA: formik.values.PIECE_AREA,
            insurancePrice: isRent ? formik.values.insurancePrice : '',
            LISTING_TYPE: formik.values.listingType || '',
          })
            .then((response) => {
              dispatch(updateFormData(response.data));
              formik.setValues({
                ...formik.values,
                piecePrice: isRent ? formik.values.rentPrice : formik.values.PIECE_PRICE,
                pieceArea: formik.values.PIECE_AREA,
                ...response.data,
              });
              setLoading(false);
            })
            .catch((error) => {
              console.error('Error adding financial data', error);
              setLoading(false);
            });
        }
        setInputChanged(false); // Reset the input change flag
      }, 500); // 500ms delay

      return () => clearTimeout(timeoutId);
    }
  }, [inputChanged, formik.values.PIECE_AREA, formik.values.PIECE_PRICE, formik.values.rentPrice, formik.values.insurancePrice]);

  return (
    <Formik
      enableReinitialize
      initialValues={formik.values}
      validationSchema={formik.validationSchema}
      onSubmit={formik.handleSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setValues }) => {
        console.log("Financial Validation Errors:", errors);
        return (
          <form onSubmit={handleSubmit}>
            <Grid rowSpacing={2} container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>

              {formik.values.listingType === 'SALE' && (
                <>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.piecePrice')}
                    </Typography>
                    <TextField
                      name="PIECE_PRICE"
                      disabled={viewMode}
                      fullWidth
                      value={formik.values.PIECE_PRICE}
                      onChange={(event) => handleFieldChange(event, values, handleChange, setValues)}
                      variant="outlined"
                      onBlur={handleBlur}
                      error={formik.touched.PIECE_PRICE && Boolean(formik.errors.PIECE_PRICE)}
                      helperText={formik.touched.PIECE_PRICE && formik.errors.PIECE_PRICE}
                    />
                  </Grid>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.unitArea')}
                    </Typography>
                    <TextField
                      name="PIECE_AREA"
                      required
                      disabled={viewMode}
                      fullWidth
                      value={formik.values.PIECE_AREA}
                      variant="outlined"
                      onChange={(event) => handleFieldChange(event, values, handleChange, setValues)}
                      onBlur={handleBlur}
                      error={formik.touched.PIECE_AREA && Boolean(formik.errors.PIECE_AREA)}
                      helperText={formik.touched.PIECE_AREA && formik.errors.PIECE_AREA}
                    />
                  </Grid>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.realEstateTax')}
                    </Typography>
                    <TextField
                      name="realEstateTax"
                      disabled
                      fullWidth
                      onChange={formik.handleChange}
                      value={formik.values.realEstateTax}
                      variant="outlined"
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.ownershipFees')}
                    </Typography>
                    <TextField
                      name="ownershipFees"
                      fullWidth
                      disabled
                      value={formik.values.ownershipFees}
                      variant="outlined"
                      onChange={formik.handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.meterPrice')}
                    </Typography>
                    <TextField
                      name="meterPrice"
                      disabled
                      fullWidth
                      value={formik.values.meterPrice}
                      onChange={handleChange}
                      variant="outlined"
                      onBlur={handleBlur}
                      error={formik.touched.meterPrice && Boolean(formik.errors.meterPrice)}
                      helperText={formik.touched.meterPrice && formik.errors.meterPrice}
                    />
                  </Grid>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.totalPrice')}
                    </Typography>
                    <TextField
                      name="totalPrice"
                      disabled
                      fullWidth
                      value={formik.values.totalPrice}
                      variant="outlined"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={formik.touched.totalPrice && Boolean(formik.errors.totalPrice)}
                      helperText={formik.touched.totalPrice && formik.errors.totalPrice}
                    />
                  </Grid>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.totalPriceNoVat')}
                    </Typography>
                    <TextField
                      name="totalPriceNoVat"
                      disabled
                      fullWidth
                      value={formik.values.totalPriceNoVat}
                      variant="outlined"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={formik.touched.totalPriceNoVat && Boolean(formik.errors.totalPriceNoVat)}
                      helperText={formik.touched.totalPriceNoVat && formik.errors.totalPriceNoVat}
                    />
                  </Grid>
                </>
              )}

              {formik.values.listingType === 'RENT' && (
                <>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.annualRentalRate')} <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                      name="rentPrice"
                      disabled={viewMode}
                      required
                      fullWidth
                      value={formik.values.rentPrice}
                      onChange={handleFieldChange}
                      variant="outlined"
                      onBlur={formik.handleBlur}
                      error={formik.touched.rentPrice && Boolean(formik.errors.rentPrice)}
                      helperText={formik.touched.rentPrice && formik.errors.rentPrice}
                    />
                  </Grid>
                  <Grid item xs={2} sm={4} md={6}>
                    <Typography variant="body1" gutterBottom>
                      {t('Units.insurancePrice')} <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                      name="insurancePrice"
                      disabled={viewMode}
                      required
                      fullWidth
                      value={formik.values.insurancePrice}
                      onChange={handleFieldChange}
                      variant="outlined"
                      onBlur={formik.handleBlur}
                      error={formik.touched.insurancePrice && Boolean(formik.errors.insurancePrice)}
                      helperText={formik.touched.insurancePrice && formik.errors.insurancePrice}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={2} sm={4} md={6}>
                <Typography variant="body1" gutterBottom>
                  {t('Units.commissionFees')}
                </Typography>
                <TextField
                  name="commissionFees"
                  disabled
                  fullWidth
                  value={formik.values.commissionFees}
                  variant="outlined"
                  onChange={formik.handleChange}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={2} sm={4} md={6}>
                <Typography variant="body1" gutterBottom>
                  {t('Units.commissionFeesVat')}
                </Typography>
                <TextField
                  name="commissionFeesVat"
                  disabled
                  fullWidth
                  value={formik.values.commissionFeesVat}
                  variant="outlined"
                  onChange={formik.handleChange}
                  onBlur={handleBlur}
                />
              </Grid>

              {loading && <CircularProgress />}

              <Grid container spacing={2} sx={{ mt: 4 }} justifyContent="space-between">
                {!viewMode && fromPage === 'review-products' && (userId === managerId || isManager === '1') && (
                  <>
                    <Grid item>
                      <Button sx={{ border: '1px solid #111827', ml: 3 }} size="large" type="button" onClick={handlePrev}>
                        {t('General.back')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Grid container spacing={2} justifyContent="flex-end">
                        <Grid item>
                          <Button
                            sx={{ border: '1px solid #111827' }}
                            size="large"
                            type="button"
                            onClick={() => handleOpenRejectDialog()}
                          >
                            {t('Units.reject')}
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button size="large" type="submit" variant="contained">
                            {t('Units.approve')}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                {viewMode && (
                  <Grid container spacing={2} justifyContent="space-between">
                    <Grid item>
                      <Button sx={{ border: '1px solid #111827' }} size="large" type="button" onClick={handlePrev}>
                        {t('Units.back') || t('General.back')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <LoadingButton
                        style={{ padding: '0.8rem' }}
                        variant="contained"
                        onClick={() => {
                          if (fromPage !== 'review-products') navigate('/dashboard/units');
                          else navigate('/dashboard/review-products');
                        }}
                      >
                        {t('Units.ok')}
                      </LoadingButton>
                    </Grid>
                  </Grid>
                )}
                {!viewMode && (fromPage !== 'review-products' || (userId !== managerId && isManager !== '1')) && (
                  <Grid container spacing={2} justifyContent="flex-end">
                    <Grid item>
                      <Button sx={{ border: '1px solid #111827' }} size="large" type="button" onClick={handlePrev}>
                        {t('Units.back')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button size="large" type="submit" variant="contained">
                        {t('Units.submit')}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {open && (
              <ModalData
                open={open}
                onClose={onClose}
                onReject={onReject}
                rejectReason={rejectReason}
                setRejectReason={setRejectReason}
              />
            )}
          </form>
        )
      }}
    </Formik>
  );
}
